import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  GetCustomMetricsParams,
  GetCustomMetricsResponse
} from '@blisspointmedia/bpm-types/dist/CustomMetrics';
import { ChartConfig, FieldMetaCtx } from '@blisspointmedia/bpm-types/dist/dashboarding';
import { awaitJSON, SingleChannelLambdaFetch } from '../../utils/fetch-utils';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import {
  buildChannelByQueryId,
  ChannelCustomMetrics,
  chartChannels,
  combineByChannel,
  toDisplayMap
} from './field-meta-ctx-utils';

// A company's custom metrics are stable for the session; 5 minutes matches the other dashboarding
// queries (useChartData, chartConfigQueryOptions).
const CUSTOM_METRICS_STALE_TIME = 5 * 60 * 1000;

// Build the field-metadata context for the chart prep layer: the company's currency, plus the custom
// metric display metadata getFieldMeta needs so a custom metric renders with its real name and
// formatting instead of its raw id.
//
// Takes the chart's config because the custom-metric half is CHANNEL-SCOPED, not company-scoped.
// `custom_metrics` is unique on (companies, name, channel), so one company can hold the same id on
// two channels — and measured over all 475 rows, 7 of the 39 such collisions carry different display
// metadata, 6 of them differing in `formatter` (`percent` on one channel vs `number` on another).
// Flattening them into one map would render a conversion rate as `0.12` instead of `12%`, so the map
// stays keyed by channel and `channelByQueryId` tells getFieldMeta which one a given field belongs to.
export const useFieldMetaCtx = (config: ChartConfig): FieldMetaCtx => {
  const { cid: company, currency_code: currencyCode } = useCompanyInfo();

  const channels = useMemo(() => chartChannels(config), [config]);
  const channelByQueryId = useMemo(() => buildChannelByQueryId(config), [config]);

  // One cache entry per (company, channel), so every chart on a dashboard reading the same channel
  // shares a single fetch rather than one per chart. Each queryFn returns its own channel alongside
  // the rows, which is what lets `combineByChannel` stay a pure module-scope function.
  const customMetrics = useQueries({
    queries: channels.map(channel => ({
      queryKey: ['custom-metrics-display', company, channel],
      enabled: Boolean(company) && Boolean(channel),
      staleTime: CUSTOM_METRICS_STALE_TIME,
      queryFn: async (): Promise<ChannelCustomMetrics> => {
        const res = await SingleChannelLambdaFetch<GetCustomMetricsParams>('/getCustomMetrics', {
          params: { company, channel }
        });
        return { channel, metrics: toDisplayMap(await awaitJSON<GetCustomMetricsResponse>(res)) };
      }
    })),
    combine: combineByChannel
  });

  return useMemo(
    () => ({ currencyCode, channelByQueryId, customMetrics }),
    [currencyCode, channelByQueryId, customMetrics]
  );
};
